import React, { useState, useRef, useContext } from "react";
import {
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { filter } from "lodash";
import { debounce } from "lodash/fp";
import Autocomplete from "@mui/material/Autocomplete";
import { titleize } from "inflection";
import { map as mapFp } from "lodash/fp";
import { useMutation, useQuery } from "@apollo/client/react";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { Report, EventTypeEnum, PermissionEntityEnum, PermissionActionEnum, CreateEventDocument, GetProjectsDocument, EventCategoryEnum } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import OverwatchModal from "../OverwatchModal";
import TimeAutocomplete from "../TimeAutocomplete";
import PermissionContext from "@/Contexts/Permissions";

const DEFAULT_EVENT_STATE = {
  category: null,
  eventType: EventTypeEnum.Present,
  description: "",
  flag: false,
  projectId: "",
  title: "",
  occurredAt: null as string | null,
};

const normalDebounce = debounce(500);
const mapNode = mapFp("node");

interface CreateEventModalProps {
  minimized?: boolean;
  handleClose: () => void;
  reportDate?: Report["reportDate"];
  project?: { id: string; title: string; code: string } | null;
  handleMinimize?: (label: string) => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({
  minimized,
  handleClose,
  handleMinimize = undefined,
  project,
  reportDate,
}) => {
  const { currentUser, dateTimePreference } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const { Projects } = PermissionEntityEnum;
  const { View } = PermissionActionEnum;

  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [event, setEvent] = useState({
    ...DEFAULT_EVENT_STATE,
    projectId: project?.id || "",
    occurredAt: reportDate ? dayjs(reportDate).toISOString() : null,
  });
  const [createEvent, { loading }] = useMutation(CreateEventDocument);

  const { data } = useQuery(GetProjectsDocument, {
    fetchPolicy: "cache-and-network",
    skip: !canPerformAction(View, Projects) || Boolean(project),
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
    },
  });

  const currentDateValue: Dayjs | null = event.occurredAt ? dayjs(event.occurredAt) : null;

  const handleDateChange = (newDate: Dayjs | null) => {
    if (!newDate) {
      setEvent((prev) => ({ ...prev, occurredAt: null }));
      return;
    }

    const prevDate = event.occurredAt ? dayjs(event.occurredAt) : dayjs();
    const updated = newDate
      .hour(prevDate.hour())
      .minute(prevDate.minute())
      .second(0)
      .millisecond(0);

    setEvent((prev) => ({
      ...prev,
      occurredAt: updated.toISOString(),
    }));
  };

  const handleTimeChange = (newTime: string | null) => {
    if (!newTime) {
      return;
    }

    const [hours, minutes] = newTime.split(":").map(Number);
    const baseDate = event.occurredAt
      ? dayjs(event.occurredAt)
      : reportDate
      ? dayjs(reportDate)
      : dayjs();

    const updated = baseDate
      .hour(hours || 0)
      .minute(minutes || 0)
      .second(0)
      .millisecond(0);

    setEvent((prev) => ({
      ...prev,
      occurredAt: updated.toISOString(),
    }));
  };

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const { category, eventType, description, flag, title, projectId, occurredAt } = event;

    createEvent({
      variables: {
        category,
        eventType,
        description,
        flag,
        projectId,
        ...(title && { title }),
        ...(occurredAt && { occurredAt }),
      },
      onCompleted: () => {
        displayNotification({
          type: "success",
          text: "Event Created Successfully",
        });
        handleClose();
      },
      onError: () => {
        displayNotification({
          type: "error",
          text: "Unable to create event, try again later.",
        });
      },
      update(cache, { data }) {
        const createdEvent = data?.createEvent;

        if (!createdEvent || !createdEvent.occurredAt) return;
        if (dayjs(createdEvent.occurredAt).format("HH:mm") === "00:00") return;

        const createdEventCacheId = cache.identify(createdEvent);

        cache.modify({
          fields: {
            getCompanyEvents(existingEvents = []) {
              return [
                createdEvent,
                ...filter(
                  existingEvents,
                  (eventRef: { __ref?: string }) =>
                    eventRef.__ref !== createdEventCacheId
                ),
              ];
            },
          },
        });
      }
    });
  };

  const handleOnChange = (key: string, value: string | EventCategoryEnum | EventTypeEnum, debounce: boolean = true) => {
    if (debounce) {
      debounceFunc.cancel();
      debounceFunc(() => {
        setEvent((prev) => ({
          ...prev,
          [key]: value,
        }));
      })

      return;
    }

    setEvent((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  return (
    <OverwatchModal
      size="md"
      handleClose={() => {
        handleClose();
        setEvent(DEFAULT_EVENT_STATE);
      }}
      title={`New Event Form${project ? ` for Project: ${project.code} | ${project.title}` : ""}`}
      caption="Fill out the details below to create a new event"
      primaryButtonAction={handleSubmit}
      primaryButtonText={loading ? "Creating..." : "Create"}
      disablePrimary={!(event.description && event.projectId) || loading}
      secondaryButtonAction={handleClose}
      secondaryButtonText="Cancel"
      {...(minimized && { minimized })}
      {...(handleMinimize && { handleMinimize: () => handleMinimize(event.title || 'New Event') })}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid
          container
          noValidate
          spacing={2}
          component="form"
          onSubmit={handleSubmit}
        >
          {!project && (
            <Grid size={12}>
              <Autocomplete
                options={mapNode(data?.getProjects?.edges || [])}
                getOptionLabel={(option) => `${option.code} | ${option.title}`}
                onChange={(_, value) => handleOnChange("projectId", value?.id || "", false)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Project"
                    error={!event.projectId}
                    helperText={!event.projectId ? "Project is required" : ""}
                  />
                )}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              value={event.category || null}
              options={Object.values(EventCategoryEnum)}
              getOptionLabel={(option) => titleize(option)}
              renderInput={(params) => (
                <TextField {...params} label="Category (Optional)" fullWidth />
              )}
              onChange={(_, value) => handleOnChange("category", value || "", false)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={Object.values(EventTypeEnum)}
              getOptionLabel={(option) => titleize(option)}
              value={event.eventType}
              onChange={(_, value) => handleOnChange("eventType", value || "", false)}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event Type"
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Title"
              defaultValue={event.title}
            onChange={(e) => { handleOnChange("title", e.target.value, false) }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              defaultValue=""
            onChange={(e) => { handleOnChange("description", e.target.value, false) }}
              error={!event.description}
              helperText={!event.description ? "Description is required" : ""}
            />
          </Grid>

          <Grid size={12} p={0}>
            <Typography>Occurred At (Optional)</Typography>
            <Grid container alignItems="flex-end" size={12} spacing={1}>
              <Grid size={6}>
                <DatePicker
                  label="Date"
                  value={currentDateValue}
                  onChange={handleDateChange}
                  format={dateTimePreference.date}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TimeAutocomplete
                  size="medium"
                  label="Time"
                  time={event.occurredAt ? dayjs(event.occurredAt).format("HH:mm") : "00:00"}
                  onTimeChange={handleTimeChange}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              sx={{ alignSelf: "flex-start" }}
            control={<Switch onChange={(_, checked) => setEvent((prev) => ({ ...prev, flag: checked }))} />}
              label="Flag this event"
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    </OverwatchModal>
  );
};

export default CreateEventModal;
